import React from "react";
import { Box, Typography } from "@mui/material";
import DocumentUpload from "../../../Components/DocumentUpload/DocumentUpload";
import SectionIconTile from "../../../Components/SectionIconTile";
import { ServiceDocsIcon } from "../../../Components/Icons/SectionIcons";
import { isRequestDocumentRequired } from "../recordReplacementHelper";

// Order the tiles are laid out in, so the two outgoing/incoming data files lead and
// the service paperwork follows. A docType absent from orgMeta is simply skipped.
const DOC_ORDER = [
    "ICAN_MYCAN",
    "TESTING_ICAN_MYCAN",
    "PACK_INSPECTION",
    "FSR",
    "OTHER",
];

// One set of supporting documents for the whole request.
//
// Replaces the old split where ICAN sat inside the outgoing pack card and pack
// inspection / testing data inside the incoming one: with several packs per request
// that would mean uploading the same files once per pack. The cannibalised
// exception for testing data is now evaluated across every pack (see
// isRequestDocumentRequired), because one file has to satisfy all of them.
export default function SupportingDocumentsSection({
    config = {},
    batteryID,
    packs = [],
    documents = {},
    errors = {},
    onDocChange = () => {},
}) {
    const { documentTypes = [], maxFileSizeMb = 25 } = config;
    const missingDocs = errors.documents || [];

    const orderedDocTypes = DOC_ORDER.map((code) =>
        (documentTypes || []).find((d) => d.code === code)
    ).filter(Boolean);

    return (
        <Box
            sx={{
                border: "1px solid #EAEAEA",
                borderRadius: "12px",
                padding: "24px 28px",
                backgroundColor: "#fff",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <SectionIconTile icon={<ServiceDocsIcon />} />
                <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1A1A1A" }}>
                    Supporting documents
                </Typography>
                <Box
                    sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#4A4AF4",
                        backgroundColor: "#EEF0FF",
                        borderRadius: "6px",
                        padding: "4px 12px",
                    }}
                >
                    Common across all packs
                </Box>
            </Box>

            <Typography sx={{ fontSize: "13px", color: "#8D8D8D", mt: "8px", mb: "18px" }}>
                Upload one set of documents for this request — they apply to every pack
                replacement above.
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: "16px",
                }}
            >
                {orderedDocTypes.map((docType) => (
                    <DocumentUpload
                        key={docType.code}
                        // The asterisk follows the request-wide rule, not this
                        // docType's raw orgMeta flag.
                        docType={{
                            ...docType,
                            required: isRequestDocumentRequired(docType, packs),
                        }}
                        batteryID={batteryID}
                        maxFileSizeMb={maxFileSizeMb}
                        value={documents[docType.code] || null}
                        error={missingDocs.includes(docType.code)}
                        onChange={onDocChange}
                    />
                ))}
            </Box>
        </Box>
    );
}
